// ProductDetail.js

import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { buildProductPath, parseProductSlug } from '../utils/productSlug';
import { doc, onSnapshot, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { StoreContext } from '../contexts/StoreContext';
import { CartContext } from '../contexts/CartContext';
import { BookmarksContext } from '../contexts/BookmarksContext';
import { ProductsContext } from '../contexts/ProductsContext';
import { AuthContext } from '../contexts/AuthContext';
import ContentUnavailableView from './ContentUnavailableView';
import ProductGridItem from './ProductGridItem';
import ProductReviewsSection from '../components/ProductReviewsSection';
import usePageView from '../hooks/usePageView';
import { showLocalPickupBanner } from '../constants/settings';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_ELEMENT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP } from '../constants/layout';
import {
  formatPrice,
  getDisplayUnitAmount,
  getFormattedProductPrice,
} from '../utils/priceFormatter';
import { StockStatus, isInStock } from '../constants/stockStatus';
import {
  FONT_TITLE3,
  FONT_HEADLINE,
  FONT_HEADLINE_MOBILE,
  FONT_SUBHEADLINE,
  FONT_BODY,
  FONT_CALLOUT,
  FONT_FOOTNOTE,
  FONT_CAPTION,
  FONT_CAPTION2,
} from '../constants/typography';


const formatDate = timestamp => {
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

function FadeInImage({ src, alt, onClick, className = '' }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className={`image-container ${className}`}>
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
      />
    </div>
  );
}

function MediaDisplay({ item, alt, onClick, className = '' }) {
  const [loaded, setLoaded] = useState(false);
  const isVideo = item.mediaType === 'video';

  if (isVideo) {
    return (
      <div className={`image-container${!loaded ? ' shimmer' : ''} ${className}`} onClick={onClick}>
        <video
          src={item.imageURL}
          onLoadedData={() => setLoaded(true)}
          className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
          muted
          playsInline
        />
        <div className="video-indicator">
          <span className="material-icons play-icon">play_circle</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`image-container${!loaded ? ' shimmer' : ''} ${className}`}>
      <img
        src={item.imageURL}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
      />
    </div>
  );
}

// Reusable collapsible section component
function CollapsibleSection({ title, isExpanded, onToggle, children }) {
  return (
    <div className="description-section">
      <div className="description-header" onClick={onToggle}>
        <h3 className="small-section-title">{title}</h3>
        <span className="material-icons arrow-icon">
          {isExpanded ? 'expand_less' : 'expand_more'}
        </span>
      </div>
      <hr className="section-divider" />
      {isExpanded && children}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const id = parseProductSlug(slug);
  const navigate = useNavigate();
  usePageView('product', id);
  const { showPrices, showRelated, showCart, showNewBadge } = useDisplayPreferences();
  const { store, loading: storeLoading } = useContext(StoreContext);
  const { addToCart, items: cartItems } = useContext(CartContext);
  const { addBookmark, removeBookmark, isBookmarked } = useContext(BookmarksContext);
  const { products: allProducts, imageItems: allImages, getCoverImage, loading: productsLoading } = useContext(ProductsContext);
  const { user } = useContext(AuthContext);
  const storePhone = store?.storeContact?.phoneNumber ?? '';
  const storeEmail = store?.storeContact?.email ?? '';

  const [fetchedProduct, setFetchedProduct] = useState(null);
  const product = fetchedProduct ?? allProducts?.find(p => p.id === id) ?? null;
  const [productImages, setProductImages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const [allCategories, setAllCategories] = useState([]);
  const [specDefinitions, setSpecDefinitions] = useState([]);
  const [specGroups, setSpecGroups] = useState([]);
  // const [loadedImages, setLoadedImages] = useState({});

  // Default to expanded
  const [showIdentifiers, setShowIdentifiers] = useState(true);
  // Default to expanded for contact
  const [showContact, setShowContact] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSpecGroups, setExpandedSpecGroups] = useState({});

  // Cart-related state
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [addToCartStatus, setAddToCartStatus] = useState(null); // null | 'adding' | 'added' | 'failed'
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!product?.modifierGroups) return;
    setExpandedGroups(prev => {
      const updated = { ...prev };
      let changed = false;
      product.modifierGroups.forEach(group => {
        if (updated[group.id] === undefined) {
          updated[group.id] = true;
          changed = true;
        }
      });
      const validIds = new Set(product.modifierGroups.map(group => group.id));
      Object.keys(updated).forEach(id => {
        if (!validIds.has(id)) {
          delete updated[id];
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [product?.modifierGroups]);

  // Initialize spec groups as expanded when specs are loaded
  useEffect(() => {
    if (!product?.specs) return;
    setExpandedSpecGroups(prev => {
      const updated = { ...prev };
      let changed = false;

      // Get all unique group IDs from specs
      const groupIds = new Set();
      Object.keys(product.specs).forEach(specId => {
        const definition = specDefinitions.find(d => d.id === specId);
        if (definition) {
          const groupId = definition.groupId || 'ungrouped';
          groupIds.add(groupId);
          if (updated[groupId] === undefined) {
            updated[groupId] = true;
            changed = true;
          }
        }
      });

      // Remove groups that are no longer valid
      Object.keys(updated).forEach(id => {
        if (!groupIds.has(id)) {
          delete updated[id];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [product?.specs, specDefinitions]);

  // Keep the detail page synchronized during the catalog expand migration.
  // `fetchedProduct` takes precedence over ProductsContext below, so a
  // one-shot read here would otherwise pin an already-open page to the legacy
  // price for the lifetime of the tab.
  useEffect(() => {
    if (!id) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, 'products', id),
      snap => {
        if (snap.exists()) {
          setFetchedProduct({ id: snap.id, ...snap.data() });
        } else {
          setFetchedProduct(null);
        }
      },
      error => {
        console.error('Error fetching product:', error);
        setFetchedProduct(null);
      },
    );
    return unsubscribe;
  }, [id]);

  // Filter images for THIS product from ProductsContext
  useEffect(() => {
    if (!id || !allImages) return;

    const filtered = allImages.filter(img => img.productID === id);
    setProductImages(filtered);

    let cover = null;
    if (product?.productImage?.imageItemID) {
      cover = allImages.find(
        img => img.id === product.productImage.imageItemID
      );
    }
    if (!cover && filtered.length > 0) {
      cover = filtered[0];
    }
    setCoverImage(cover);
  }, [id, allImages, product?.productImage?.imageItemID]);

  // Update document title
  useEffect(() => {
    if (product) {
      document.title = product.productName;
    }
  }, [product]);

  // Fetch ALL categories (once)
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'categories'));
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllCategories(fetched);
    })();
  }, []);

  // Fetch spec definitions
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'spec_definitions'));
      const fetched = snap.docs.map(d => {
        const data = d.data();
        // Convert options from {id: value} to [{id, value}]
        let options = null;
        if (data.options) {
          options = Object.entries(data.options).map(([id, value]) => ({ id, value }));
        }
        return {
          id: d.id,
          label: data.label || '',
          dataType: data.data_type || 'string',
          groupId: data.group_id || null,
          options
        };
      });
      setSpecDefinitions(fetched);
    })();
  }, []);

  // Fetch spec groups
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'spec_groups'));
      const fetched = snap.docs.map(d => ({
        id: d.id,
        label: d.data().label || ''
      }));
      setSpecGroups(fetched);
    })();
  }, []);

  // Modal: ESC key listener & body overflow
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape' && previewImage) {
        closeModal();
      }
    };

    if (previewImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [previewImage]);

  const handleImageClick = image => {
    setPreviewImage(image);
    setImageLoading(true);
    setTimeout(() => {
      setIsModalVisible(true);
    }, 10);
  };

  const handleModalImageLoad = () => {
    setImageLoading(false);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setImageLoading(false);
    setTimeout(() => {
      setPreviewImage(null);
    }, 200);
  };

  const handleModalClick = e => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const toggleModifierGroup = groupId => {
    setExpandedGroups(prev => {
      const isExpanded = prev?.[groupId] ?? true;
      return { ...prev, [groupId]: !isExpanded };
    });
  };

  const toggleSpecGroup = groupId => {
    setExpandedSpecGroups(prev => {
      const isExpanded = prev?.[groupId] ?? true;
      return { ...prev, [groupId]: !isExpanded };
    });
  };

  // Calculate total quantity already in cart for this product
  const getQuantityInCart = () => {
    if (!product) return 0;
    return cartItems
      .filter(item => item.productID === product.id)
      .reduce((total, item) => total + item.quantity, 0);
  };

  const quantityInCart = getQuantityInCart();

  // Handle adding product to cart
  const handleAddToCart = async () => {
    if (!product) return;

    setAddToCartStatus('adding');

    // Prepare product with image URL
    const productWithImage = {
      ...product,
      imageURL: coverImage?.imageURL || product.imageURL,
    };

    // Add to cart with current selections and quantity
    const added = await addToCart(productWithImage, selectedModifiers, quantity);
    if (!added) {
      setAddToCartStatus('failed');
      setTimeout(() => setAddToCartStatus(null), 2000);
      return;
    }

    // Show success state
    setTimeout(() => {
      setAddToCartStatus('added');
    }, 600);

    // Reset to normal state and reset quantity
    setTimeout(() => {
      setAddToCartStatus(null);
      setQuantity(1);
    }, 1500);
  };

  // Check if product is deactivated
  const isDeactivated = product?.deactivatedDate != null;

  // Check if product is in stock using enum
  const productIsInStock = isInStock(product?.stockStatus);

  // Check if all required modifiers are selected
  const areRequiredModifiersSelected = () => {
    if (!product?.modifierGroups || product.modifierGroups.length === 0) {
      return true;
    }

    return product.modifierGroups.every(group => {
      const picked = selectedModifiers.filter(m => m.groupID === group.id).length;
      const minRequired = group.quantityInfo?.minPermitted || 0;
      return picked >= minRequired;
    });
  };

  // Check if add to cart button should be disabled
  const isAddToCartDisabled =
    addToCartStatus === 'adding' ||
    isDeactivated ||
    !productIsInStock ||
    !areRequiredModifiersSelected();

  // Handle bookmark toggle
  const handleBookmarkToggle = () => {
    if (!product) return;

    if (isBookmarked(product.id)) {
      removeBookmark(product.id);
    } else {
      addBookmark(product.id);
    }
  };

  const isProductBookmarked = product ? isBookmarked(product.id) : false;

  // const handleImageLoad = productId => {
  //   setLoadedImages(prev => ({ ...prev, [productId]: true }));
  // };

  // Helper: format spec value based on data type
  const formatSpecValue = (value, definition) => {
    if (!definition) return String(value);

    switch (definition.dataType) {
      case 'boolean':
        return value ? 'Yes' : 'No';

      case 'number':
        return String(value);

      case 'string':
        return String(value);

      case 'single_select':
        if (definition.options) {
          const option = definition.options.find(opt => opt.id === value);
          return option ? option.value : value;
        }
        return value;

      case 'multi_select':
        if (Array.isArray(value) && definition.options) {
          return value
            .map(id => {
              const option = definition.options.find(opt => opt.id === id);
              return option ? option.value : id;
            })
            .join(', ');
        }
        return Array.isArray(value) ? value.join(', ') : value;

      default:
        return String(value);
    }
  };

  const isLoading = productsLoading && !product;

  const isNew = (() => {
    if (!product?.createdDate) return false;
    const created = product.createdDate.toDate ? product.createdDate.toDate() : new Date(product.createdDate);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return created > thirtyDaysAgo;
  })();

  const emailSubject = product
    ? encodeURIComponent(`Question about ${product.productName}`)
    : '';

  // Build a map: productID → [categoryID, …]
  const productToCategories = {};
  allCategories.forEach(cat => {
    (cat.itemIDs || []).forEach(pid => {
      if (!productToCategories[pid]) productToCategories[pid] = [];
      productToCategories[pid].push(cat.id);
    });
  });

  // Current product's categories (array of category IDs)
  const currCats = product ? productToCategories[product.id] || [] : [];

  // Current product's price
  const currPrice = getDisplayUnitAmount(product?.priceInfo) ?? 0;

  // Compute min/max price over all products
  const prices = allProducts.map(p => getDisplayUnitAmount(p.priceInfo) ?? 0);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  // Weights (sum should be 1)
  const weightPrice = 0.4;
  const weightCategory = 0.6;

  // Compute "suggestedProducts" by price / category similarity
  const suggestedProducts = product
    ? allProducts
        .filter(p => p.id !== product.id)
        .map(p => {
          const otherPrice = getDisplayUnitAmount(p.priceInfo) ?? 0;
          const priceDiff = Math.abs(currPrice - otherPrice);
          const priceNormalized = (maxPrice - minPrice) > 0
            ? priceDiff / (maxPrice - minPrice)
            : 0;
          const priceScore = 1 - priceNormalized;

          const otherCats = productToCategories[p.id] || [];
          const sharedCount = currCats.filter(c => otherCats.includes(c)).length;
          const unionCount = new Set([...currCats, ...otherCats]).size;
          const catScore = unionCount > 0 ? sharedCount / unionCount : 0;

          const combinedScore = weightPrice * priceScore + weightCategory * catScore;
          return { ...p, combinedScore };
        })
        .sort((a, b) => b.combinedScore - a.combinedScore)
        .slice(0, 6)
    : [];

  if (isLoading) return null;

  if (isDeactivated) {
    return (
      <ContentUnavailableView
        icon="remove_shopping_cart"
        title="Item Unavailable"
        description="This item is no longer available."
        actions={[{ label: 'Go Back', onClick: () => navigate(-1) }]}
      />
    );
  }

  return (
    <div className="product-detail-container">
      <>
      {/* Main Product Section */}
      <div className="product-main-section">
        {/* Left Column: Images */}
        <div className="images-column">
          {/* Cover Image */}
          {coverImage?.imageURL ? (
            <div className="cover-image-container">
              <MediaDisplay
                item={coverImage}
                alt={product.productName}
                onClick={() => handleImageClick(coverImage)}
                className="cover-image"
              />
            </div>
          ) : (
            <div className="cover-image-container">
              <div className="cover-placeholder">
                <span className="material-icons placeholder-icon">photo</span>
              </div>
            </div>
          )}

          {/* More Photos Gallery */}
          {productImages.length > 1 && (
            <div className="gallery-section">
              <div className="product-image-grid">
                {productImages.map(img => (
                  <div key={img.id} className="product-image-item">
                    <MediaDisplay
                      item={img}
                      alt={img.caption}
                      onClick={() => handleImageClick(img)}
                      className="gallery-image thumbnail-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Product Info */}
        <div className="product-info">
          {/* Local Pickup Banner */}
          {showLocalPickupBanner && (
            <div className="pickup-banner">
              <span className="material-icons pickup-icon">store</span>
              <span className="pickup-text">Local pickup only • Shipping not available</span>
            </div>
          )}

          {/* Product header: info stack + bookmark button */}
          <div className="product-header-row">
            <div className="product-header-text">
              {currCats.length > 0 && (
                <div className="product-categories">
                  {currCats.map(catId => {
                    const catObj = allCategories.find(c => c.id === catId);
                    return catObj ? (
                      <Link
                        key={catObj.id}
                        to={`/category/${catObj.id}`}
                        className="category-label"
                      >
                        {catObj.title}
                      </Link>
                    ) : null;
                  })}
                </div>
              )}
              <h1 className="product-title">
                {product.productName}
                {showNewBadge && isNew && (
                  <span className="new-badge">New</span>
                )}
              </h1>
              {showPrices && getFormattedProductPrice(product) ? (
                <p className="product-price">
                  {getFormattedProductPrice(product)}
                </p>
              ) : null}
            </div>
            {user && (
              <button
                onClick={handleBookmarkToggle}
                className="bookmark-button"
                aria-label={isProductBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
              >
                <span className="material-icons">
                  {isProductBookmarked ? 'bookmark' : 'bookmark_border'}
                </span>
              </button>
            )}
          </div>

          {product.stockStatus && product.stockStatus !== StockStatus.IN_STOCK && (
            <p className="stock-status">{product.stockStatus}</p>
          )}

          {showPrices && (
            <div className="add-to-cart-section">
              {(isDeactivated || !productIsInStock) && (
                <p className="stock-message">
                  {isDeactivated ? 'Unavailable' : (product.stockStatus || StockStatus.OUT_OF_STOCK)}
                </p>
              )}

              {showCart && productIsInStock && !isDeactivated && (
                <div className="quantity-row">
                  <span className="quantity-label">Quantity</span>
                  <div className="quantity-controls">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="quantity-button"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="quantity-button"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {showCart && (
                <div className="cart-controls-row">
                  <button
                    className="add-to-cart-button"
                    onClick={handleAddToCart}
                    disabled={isAddToCartDisabled}
                  >
                    {addToCartStatus === 'adding' ? (
                      <span className="button-content">
                        <span className="spinner"></span>
                        Adding...
                      </span>
                    ) : addToCartStatus === 'added' ? (
                      <span className="button-content">
                        <span className="material-icons check-icon">check_circle</span>
                        Added!
                      </span>
                    ) : addToCartStatus === 'failed' ? (
                      <span className="button-content">
                        Couldn&apos;t Add — Try Again
                      </span>
                    ) : (
                      <span className="button-content-column">
                        <span className="button-main-row">
                          {isDeactivated || !productIsInStock ? 'Sold Out' : 'Add to Cart'}
                        </span>
                        {quantityInCart > 0 && productIsInStock && !isDeactivated && (
                          <span className="cart-quantity-badge">
                            {quantityInCart} in cart
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Collapsible Description */}
          {product.description && product.description.trim() !== '' && (
            <>
              <hr className="product-main-divider" />
              <div className="description-static-section">
                <h3 className="small-section-title">About this product</h3>
                <p className="product-description">{product.description}</p>
              </div>
            </>
          )}

          {Array.isArray(product.modifierGroups) &&
            product.modifierGroups.length > 0 && (
              <div className="modifier-groups-section">
                {product.modifierGroups.map(group => {
                  const isExpanded = expandedGroups[group.id] ?? true;
                  return (
                    <CollapsibleSection
                      key={group.id}
                      title={group.title}
                      isExpanded={isExpanded}
                      onToggle={() => toggleModifierGroup(group.id)}
                    >
                      {Array.isArray(group.modifierOptions) && (
                        <ul className="modifier-options-list">
                          {group.modifierOptions.map(option => {
                            const priceInfo = option.priceInfo || {};
                            const optionUnitAmount = getDisplayUnitAmount(priceInfo);
                            const showOptionPrice =
                              showPrices &&
                              optionUnitAmount != null &&
                              optionUnitAmount > 0 &&
                              priceInfo.currency;
                            const stockStatus =
                              typeof option.stockStatus === 'string'
                                ? option.stockStatus.trim()
                                : '';
                            const isOptionOutOfStock = stockStatus === StockStatus.OUT_OF_STOCK;
                            return (
                              <li key={option.id} className="modifier-option-item">
                                <div className="modifier-option-header">
                                  <span className="modifier-option-title">
                                    {option.title}
                                  </span>
                                  {showOptionPrice && (
                                    <span className="modifier-option-price">
                                      {formatPrice(optionUnitAmount, priceInfo.currency)}
                                    </span>
                                  )}
                                </div>
                                {isOptionOutOfStock && (
                                  <p className="modifier-option-stock modifier-option-stock--out">
                                    {stockStatus || option.stockStatus}
                                  </p>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </CollapsibleSection>
                  );
                })}
              </div>
            )}

          {/* ----------------- "Product Specs" SECTIONS (each group is its own section) ----------------- */}
          {product.specs && Object.keys(product.specs).length > 0 && (() => {
            // Build groups: { groupId: [...specs], null: [...ungrouped specs] }
            const grouped = {};
            Object.entries(product.specs).forEach(([specId, value]) => {
              const definition = specDefinitions.find(d => d.id === specId);
              if (definition) {
                const groupId = definition.groupId || 'ungrouped';
                if (!grouped[groupId]) grouped[groupId] = [];
                grouped[groupId].push({ specId, value, definition });
              }
            });

            return Object.entries(grouped).map(([groupId, specs]) => {
              const group = groupId !== 'ungrouped' ? specGroups.find(g => g.id === groupId) : null;
              const title = group ? group.label : 'Specifications';
              const isExpanded = expandedSpecGroups[groupId] ?? true;

              return (
                <CollapsibleSection
                  key={groupId}
                  title={title}
                  isExpanded={isExpanded}
                  onToggle={() => toggleSpecGroup(groupId)}
                >
                  <ul className="product-specs-list">
                    {specs.map(({ specId, value, definition }) => (
                      <li key={specId}>
                        <strong>{definition.label}:</strong>{' '}
                        {formatSpecValue(value, definition)}
                      </li>
                    ))}
                  </ul>
                </CollapsibleSection>
              );
            });
          })()}

          {/* ----------------- "Identifiers" SECTION (only show non-empty rows) ----------------- */}
          {product.identifiers && (
            <CollapsibleSection
              title="Identifiers"
              isExpanded={showIdentifiers}
              onToggle={() => setShowIdentifiers(prev => !prev)}
            >
              <ul className="product-specs-list">
                {product.identifiers.sku && product.identifiers.sku.trim() !== '' && (
                  <li>
                    <strong>SKU:</strong> {product.identifiers.sku}
                  </li>
                )}
                {product.identifiers.upc && product.identifiers.upc.trim() !== '' && (
                  <li>
                    <strong>UPC:</strong> {product.identifiers.upc}
                  </li>
                )}
                {product.identifiers.ean && product.identifiers.ean.trim() !== '' && (
                  <li>
                    <strong>EAN:</strong> {product.identifiers.ean}
                  </li>
                )}
                {product.identifiers.gtin && product.identifiers.gtin.trim() !== '' && (
                  <li>
                    <strong>GTIN:</strong> {product.identifiers.gtin}
                  </li>
                )}
              </ul>
            </CollapsibleSection>
          )}

          {product.deactivatedDate && (
            <p className="deactivated-date">
              No longer available as of {formatDate(product.deactivatedDate)}
            </p>
          )}

          {/* Contact Section — temporarily hidden
          {(storePhone || storeEmail) && (
            <CollapsibleSection
              title="Have more questions?"
              isExpanded={showContact}
              onToggle={() => setShowContact(!showContact)}
            >
              <div className="contact-button-row">
                {storePhone && (
                  <button
                    type="button"
                    className="call-store-button"
                    onClick={() => {
                      const digitsOnly = storePhone.replace(/\D+/g, '');
                      window.location.href = `tel:${digitsOnly}`;
                    }}
                    disabled={storeLoading}
                  >
                    {storeLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <span className="material-icons phone-icon">
                          phone
                        </span>
                        Call
                      </>
                    )}
                  </button>
                )}
                {storePhone && (
                  <button
                    type="button"
                    className="text-store-button"
                    onClick={() => {
                      const digitsOnly = storePhone.replace(/\D+/g, '');
                      window.location.href = `sms:${digitsOnly}`;
                    }}
                    disabled={storeLoading}
                  >
                    {storeLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <span className="material-icons text-icon">
                          textsms
                        </span>
                        Text
                      </>
                    )}
                  </button>
                )}
                {storeEmail && (
                  <button
                    type="button"
                    className="email-store-button"
                    onClick={() => {
                      window.location.href = `mailto:${storeEmail}?subject=${emailSubject}`;
                    }}
                    disabled={storeLoading}
                  >
                    {storeLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <span className="material-icons email-icon">mail</span>
                        Email
                      </>
                    )}
                  </button>
                )}
              </div>
            </CollapsibleSection>
          )}
          */}
        </div>
      </div>

      {/* Product Reviews Section */}
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '0 auto', padding: '0 20px' }}>
        <ProductReviewsSection productId={id} />
      </div>

      {/* Suggested Products Section */}
      {showRelated && suggestedProducts.length > 0 && (
        <>
          <hr className="suggested-divider" />
          <div className="suggested-section">
            <div className="section-header">
              <h2 className="section-title">You May Also Like</h2>
            </div>
            <div className="suggested-grid">
              {suggestedProducts.map(sugg => (
                <ProductGridItem
                  key={sugg.id}
                  product={sugg}
                  coverImage={getCoverImage(sugg)}
                  onClick={() => navigate(buildProductPath(sugg))}
                  isNew={(() => {
                    if (!sugg?.createdDate) return false;
                    const created = sugg.createdDate.toDate ? sugg.createdDate.toDate() : new Date(sugg.createdDate);
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    return created > thirtyDaysAgo;
                  })()}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Image/Video Preview Modal */}
      {previewImage && (
        <div
          className={`modal-overlay ${
            isModalVisible ? 'modal-visible' : ''
          }`}
          onClick={handleModalClick}
        >
          <button
            className="modal-close-button"
            onClick={closeModal}
            aria-label="Close preview"
          >
            ×
          </button>

          <div
            className={`modal-content ${
              isModalVisible ? 'modal-content-visible' : ''
            }`}
          >
            {imageLoading && (
              <div className="modal-placeholder">
                <div className="placeholder-spinner"></div>
                <p className="placeholder-text">Loading {previewImage.mediaType === 'video' ? 'video' : 'image'}...</p>
              </div>
            )}

            {previewImage.mediaType === 'video' ? (
              <video
                src={previewImage.imageURL}
                controls
                autoPlay
                className={`modal-image ${
                  imageLoading ? 'modal-image-loading' : ''
                }`}
                onLoadedData={handleModalImageLoad}
              />
            ) : (
              <img
                src={previewImage.imageURL}
                alt={previewImage.caption || product.productName}
                className={`modal-image ${
                  imageLoading ? 'modal-image-loading' : ''
                }`}
                onLoad={handleModalImageLoad}
              />
            )}
            {previewImage.caption && !imageLoading && (
              <p className="modal-caption">{previewImage.caption}</p>
            )}
          </div>
        </div>
      )}

      </>
      <style jsx>{`
        /* Container */
        .product-detail-container {
          --product-detail-space: 16px;
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 767px) {
          .product-detail-container {
            --product-detail-space: ${SPACING_COMPONENT};
            padding: ${SPACING_BUTTON};
            padding-left: max(${SPACING_BUTTON}, env(safe-area-inset-left));
            padding-right: max(${SPACING_BUTTON}, env(safe-area-inset-right));
          }
        }

        /* Main Section: flex-column on mobile, row on desktop */
        .product-main-section {
          display: flex;
          gap: var(--product-detail-space);
          margin-bottom: var(--product-detail-space);
          flex-direction: column;
        }
        @media (min-width: 768px) {
          .product-main-section {
            flex-direction: row;
            align-items: flex-start;
          }
        }

        /* Images Column - Contains both cover image and gallery */
        .images-column {
          flex: 1;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: var(--product-detail-space);
        }
        @media (max-width: 767px) {
          .images-column {
            max-width: none;
            width: 100%;
          }
        }

        /* Cover Image Container */
        .cover-image-container {
          width: 100%;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        /* Placeholder when no cover image */
        .cover-placeholder {
          position: relative;
          width: 100%;
          background: #3a3a3a;
          border: 1px solid var(--color-border-strong);
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
        }
        .cover-placeholder::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
        .cover-placeholder .placeholder-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 64px;
          opacity: 0.5;
          line-height: 1;
        }

        /* Image-Container & Fade-In */
        .image-container {
          position: relative;
          width: 100%;
          cursor: pointer;
          transition: transform 0.2s ease;
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
          border: 1px solid var(--color-border-strong);
          background: #3a3a3a;
        }
        .image-container.shimmer {
          background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
        .image-container:hover {
          transform: translateY(-2px);
        }
        .image-container::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
        /* Thumbnails */
        .thumbnail-image::before {
          padding-top: 100% !important;
        }
        .fade-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        .fade-img.loaded {
          opacity: 1;
        }
        /* Video elements */
        video.fade-img {
          background: #000;
        }
        .clickable-image {
          cursor: pointer;
        }

        /* Video Indicator Overlay */
        .video-indicator {
          position: absolute;
          bottom: 8px;
          left: 8px;
          background: rgba(0, 0, 0, 0.6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }
        .play-icon {
          font-size: 32px;
          color: white;
          line-height: 1;
        }

        /* Product Info (Title, Price) */
        .product-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: var(--product-detail-space);
        }
        @media (min-width: 768px) {
          /* Desktop rhythm mirrors SwiftUI-style VStack spacing. */
          .product-info > .pickup-banner,
          .product-info > .product-header-row,
          .product-info > .stock-status,
          .product-info > .description-section,
          .product-info > .description-static-section,
          .product-info > .product-main-divider,
          .product-info > .modifier-groups-section,
          .product-info > .deactivated-date {
            margin-top: 0;
            margin-bottom: 0;
          }
        }

        /* Local Pickup Banner */
        .pickup-banner {
          display: flex;
          align-items: center;
          gap: ${SPACING_ELEMENT};
          background-color: #f0f4f8;
          border: 1px solid #cbd5e0;
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_COMPACT} ${SPACING_BUTTON};
          margin: 0;
          font-size: ${FONT_CALLOUT};
          color: #2d3748;
          width: 100%;
          box-sizing: border-box;
        }
        .pickup-icon {
          font-size: 20px;
          line-height: 1;
          color: #4a5568;
        }
        .pickup-text {
          line-height: 1.4;
          font-weight: 400;
        }

        /* New: Category labels just above title */
        .product-categories {
          margin: 0;
        }
        .category-label {
          display: inline-block;
          font-size: ${FONT_CAPTION};
          font-weight: 400;
          letter-spacing: 0.06em;
          color: var(--color-text-muted);
          margin-right: ${SPACING_COMPACT};
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .category-label:hover,
        .category-label:focus {
          color: var(--color-text-secondary);
          text-decoration: underline;
        }

        .product-header-row {
          display: flex;
          align-items: flex-start;
          gap: ${SPACING_COMPONENT};
          margin: 0;
        }

        .product-header-text {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .product-title {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          line-height: 1.3;
          color: var(--color-text-primary);
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .new-badge {
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .bookmark-button {
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
        }

        .bookmark-button:hover:not(:disabled) {
          background: none;
          transform: scale(1.05);
        }

        .bookmark-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .bookmark-button .material-icons {
          font-size: 24px;
          color: var(--color-accent);
          transition: color 0.2s ease;
        }

        .bookmark-button:hover:not(:disabled) .material-icons {
          color: var(--color-accent-hover);
        }

        .product-price {
          margin: 0;
          font-size: ${FONT_BODY};
          font-weight: 400;
          line-height: 1.3;
          color: var(--color-text-secondary);
        }

        /* Stock Status & Contact Buttons */
        .stock-status {
          margin: 0;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-muted);
        }
        .contact-button-row {
          display: flex;
          flex-wrap: wrap;
          gap: ${SPACING_COMPONENT};
          margin-top: ${SPACING_COMPONENT};
        }
        .call-store-button,
        .text-store-button,
        .email-store-button {
          background-color: var(--color-accent);
          border: none;
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_COMPONENT} ${SPACING_CARD};
          font-size: ${FONT_HEADLINE};
          color: var(--color-accent-text);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_ELEMENT};
        }
        .call-store-button:hover:not(:disabled),
        .text-store-button:hover:not(:disabled),
        .email-store-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
        }
        .call-store-button:disabled,
        .text-store-button:disabled,
        .email-store-button:disabled {
          opacity: 0.6;
          cursor: default;
          background-color: var(--color-badge-muted);
        }
        .phone-icon,
        .text-icon,
        .email-icon {
          font-size: 20px;
          line-height: 1;
        }
        /* Collapsible Description */
        .description-section {
          margin: 0;
        }
        .description-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          margin-bottom: var(--product-detail-space);
        }
        .small-section-title {
          margin: 0;
          font-size: ${FONT_HEADLINE};
          font-weight: 600;
          line-height: 1.2;
          color: var(--color-text-primary);
        }
        .arrow-icon {
          font-size: 24px;
          line-height: 1;
        }
        /* Divider now solid */
        .section-divider {
          margin: 0 0 var(--product-detail-space) 0;
          height: 2px;
          border: 0;
          background-color: var(--color-border);
        }

        .product-main-divider {
          margin: 0;
          height: 2px;
          border: 0;
          background-color: var(--color-border);
          width: 100%;
        }

        .description-static-section {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: var(--product-detail-space);
          margin: 0;
        }

        .product-description {
          margin: 0;
          font-size: ${FONT_BODY};
          line-height: 1.65;
          color: var(--color-text-secondary);
        }

        @media (max-width: 767px) {
          .category-label {
            font-size: ${FONT_CAPTION};
          }

          .product-title {
            font-size: ${FONT_HEADLINE};
          }

          .product-price {
            font-size: ${FONT_CALLOUT};
          }

          .small-section-title {
            font-size: ${FONT_CALLOUT};
          }

          .product-description {
            font-size: ${FONT_CALLOUT};
            line-height: 1.6;
          }
        }

        /* New: Product Specifications List */
        .product-specs-list {
          margin: 0;
          padding-left: ${SPACING_BUTTON}; /* indent list items */
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          line-height: 1.6;
        }
        .product-specs-list li {
          margin-bottom: ${SPACING_ELEMENT};
        }
        .product-specs-list li strong {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .modifier-options-list {
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .modifier-option-item {
          padding: ${SPACING_COMPONENT} 0;
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .modifier-option-item:last-child {
          border-bottom: none;
        }
        .modifier-option-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: ${SPACING_COMPONENT};
        }
        .modifier-option-title {
          font-size: ${FONT_BODY};
          font-weight: 400;
          color: var(--color-text-primary);
        }
        .modifier-option-price {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          white-space: nowrap;
        }
        .modifier-option-stock {
          margin: 6px 0 0 0;
          font-size: ${FONT_CALLOUT};
          color: var(--color-text-secondary);
        }
        .modifier-option-stock--out {
          color: var(--color-text-muted);
        }

        .deactivated-date {
          margin: ${SPACING_ELEMENT} 0;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-muted);
        }

        /* Add to Cart Section */
        .add-to-cart-section {
        }

        .signed-out-notice {
          background-color: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_BUTTON};
          margin-bottom: ${SPACING_BUTTON};
        }

        .notice-content {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPONENT};
        }

        .notice-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .notice-text {
          flex: 1;
        }

        .notice-title {
          font-size: ${FONT_BODY};
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #92400e;
        }

        .notice-description {
          font-size: ${FONT_CALLOUT};
          margin: 0;
          color: #92400e;
        }

        .stock-message {
          text-align: center;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          margin-bottom: ${SPACING_COMPONENT};
        }

        .cart-controls-row {
          display: flex;
          align-items: stretch;
          gap: ${SPACING_COMPONENT};
        }

        .quantity-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .quantity-label {
          font-weight: 400;
          color: var(--color-text-primary);
          font-size: ${FONT_BODY};
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPONENT};
          padding: 4px;
          height: 54px;
        }

        .quantity-button {
          width: 36px;
          height: 36px;
          border: none;
          background-color: var(--color-surface-muted);
          color: var(--color-text-primary);
          font-size: ${FONT_HEADLINE};
          cursor: pointer;
          border-radius: 50%;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 400;
        }

        .quantity-button:hover {
          background-color: var(--color-border);
        }

        .quantity-display {
          min-width: 32px;
          text-align: center;
          font-weight: 400;
          color: var(--color-text-primary);
          font-size: ${FONT_BODY};
        }

        .add-to-cart-button {
          width: 100%;
          padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY};
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 44px;
        }

        .add-to-cart-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
          transform: translateY(-1px);
        }

        .add-to-cart-button:disabled {
          background-color: var(--color-surface-muted);
          color: var(--color-text-secondary);
          cursor: not-allowed;
          opacity: 0.6;
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .button-content-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .button-main-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .cart-quantity-badge {
          font-size: ${FONT_CAPTION2};
          font-weight: 600;
          color: var(--color-accent-text);
          opacity: 0.9;
        }

        .plus-icon {
          font-size: 20px;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid var(--color-accent-text);
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        .check-icon {
          font-size: 20px;
          color: var(--color-accent-text);
          line-height: 1;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Gallery Section */
        .gallery-section {
          margin-top: 0;
        }
        .product-image-grid {
          display: grid;
          gap: 6px;
          grid-template-columns: repeat(4, 1fr);
        }
        @media (min-width: 600px) {
          .product-image-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 14px;
          }
        }
        @media (max-width: 480px) {
          .product-image-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
          }
        }
        .product-image-item {
          display: flex;
          flex-direction: column;
        }

        /* Suggested Products Section */
        .suggested-divider {
          margin: var(--product-detail-space) 0;
          height: 2px;
          border: 0;
          background-color: var(--color-border);
          width: 100%;
        }
        .suggested-section {
          display: flex;
          flex-direction: column;
          gap: var(--product-detail-space);
        }
        .section-title {
          margin: 0;
          font-size: ${FONT_HEADLINE};
          font-weight: 600;
        }
        .suggested-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 6px;
          width: 100%;
        }
        @media (max-width: 999px) {
          .suggested-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        @media (max-width: 600px) {
          .suggested-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* Modal Overlay & Content */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 6px;
          opacity: 0;
          transition: opacity 0.2s ease, background-color 0.2s ease;
          pointer-events: none;
        }
        .modal-overlay.modal-visible {
          opacity: 1;
          background-color: rgba(0, 0, 0, 0.9);
          pointer-events: all;
        }

        .modal-close-button {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(0, 0, 0, 0.7);
          border: none;
          color: white;
          font-size: 32px;
          cursor: pointer;
          padding: 6px 12px;
          z-index: 1001;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s ease, transform 0.2s.ease;
          line-height: 1;
        }
        .modal-close-button:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }

        .modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          transform: scale(0.8);
          opacity: 0;
          transition: transform 0.2s.ease, opacity 0.2s.ease;
        }
        .modal-content.modal-content-visible {
          transform: scale(1);
          opacity: 1;
        }

        .modal-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 300px;
          min-height: 200px;
          color: white;
        }
        .placeholder-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 10px;
        }
        .placeholder-text {
          font-size: ${FONT_BODY};
          opacity: 0.8;
          margin: 0;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .modal-image {
          max-width: 100%;
          max-height: 80vh;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: 0;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          border: 1px solid var(--color-border-strong);
          transition: opacity 0.2s ease;
        }
        .modal-image-loading {
          opacity: 0;
          position: absolute;
          pointer-events: none;
        }
        .modal-caption {
          margin-top: 10px;
          color: white;
          font-size: ${FONT_BODY};
          text-align: center;
          max-width: 600px;
          line-height: 1.4;
        }

        /* Scope border-box to product detail only -- a global rule here
           leaks into the navbar and other shared components while this
           route is mounted, causing layout width mismatches. */
        .product-detail-container,
        .product-detail-container *,
        .product-detail-container *::before,
        .product-detail-container *::after {
          box-sizing: border-box;
        }

        .suggested-product-content,
        .suggested-product-info {
          min-width: 0;
        }

        .suggested-product-price {
          margin-left: auto;
        }
        @media (max-width: 600px) {
          .modal-close-button {
            top: 15px;
            right: 15px;
            font-size: 24px;
            width: 40px;
            height: 40px;
            padding: 6px 8px;
          }
          .modal-image {
            max-height: 75vh;
          }
          .modal-caption {
            font-size: ${FONT_CALLOUT};
            margin-top: 10px;
          }
          .modal-placeholder {
            min-width: 250px;
            min-height: 150px;
          }
          .placeholder-spinner {
            width: 32px;
            height: 32px;
            margin-bottom: 10px;
          }
          .placeholder-text {
            font-size: ${FONT_CALLOUT};
          }
        }
      `}</style>
    </div>
  );
}
